import { useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/endpoints';
import type { CustomerCard, Pool, Station } from '../api/types';
import { useCountUp, useCountdown, useDeadline, useElapsed } from '../hooks/useCountdown';
import {
  formatBalance,
  formatCountdown,
  formatMinutes,
  formatMoney,
  formatTime,
  poolForStationType,
  STATION_TYPE_LABEL,
} from '../lib/format';
import { AddPs5TimeDialog } from './AddPs5TimeDialog';
import { AddItemDialog, CustomerTabSection } from './CustomerTab';
import { useAuth } from '../auth/AuthContext';
import { SellRechargeDialog } from './SellRechargeDialog';
import { ShutdownDialog } from './ShutdownDialog';
import { agentFault, isUnblocked, stationState } from './StationList';
import { useToast } from './Toast';
import { Badge, Button, Card, Modal } from './ui';

// How long a free seat gets opened for. Short on purpose: the timer exists so
// an opened PC is never forgotten, and "8 hours" is the ceiling, not a preset.
const UNBLOCK_PRESETS = [15, 30, 60, 120];

// Both halves of the question staff get asked at the counter: how long have
// they been on, and how long have they got. Each to the second, because the
// station's own on-screen clock shows seconds and the two must agree. Both
// hold still while the session is paused.
function SessionClocks({ station }: { station: Station }) {
  const session = station.active_session!;
  const running = !session.paused;
  const remaining = useCountdown(
    session.seconds_remaining ?? (session.minutes_remaining ?? 0) * 60,
    session.session_id,
    running,
  );
  // seconds_played is the server's figure with pauses taken out; wall-clock
  // from start_time is the fallback for a backend that predates it.
  const hasServerPlayed =
    session.seconds_played !== null && session.seconds_played !== undefined;
  const playedServer = useCountUp(session.seconds_played, session.session_id, running);
  const playedWall = useElapsed(session.start_time);
  const played = hasServerPlayed ? playedServer : playedWall;
  const pausedFor = useElapsed(session.paused_at ?? session.start_time);

  return (
    <div className="grid grid-cols-2 gap-3">
      <Card>
        <p className="text-xs uppercase tracking-wide text-slate-500">Played</p>
        <p className="mt-1 font-mono text-3xl font-semibold tabular-nums text-slate-200">
          {formatCountdown(played)}
        </p>
        {session.paused_seconds > 0 && !session.paused && (
          <p className="mt-1 text-xs text-slate-500">
            paused {formatBalance(session.paused_seconds)} in total
          </p>
        )}
      </Card>
      <Card
        className={
          session.paused
            ? 'border-amber-500/50 bg-amber-500/10'
            : session.warning_active
              ? 'border-rose-500/60 bg-rose-500/10'
              : 'border-emerald-500/40 bg-emerald-500/5'
        }
      >
        <p className="text-xs uppercase tracking-wide text-slate-500">Left</p>
        <p
          className={`mt-1 font-mono text-3xl font-semibold tabular-nums ${
            session.paused
              ? 'text-amber-300'
              : session.warning_active
                ? 'text-rose-300'
                : 'text-emerald-300'
          }`}
        >
          {formatCountdown(remaining)}
        </p>
        {session.paused && (
          <p className="mt-1 text-xs text-amber-200">
            paused for {formatCountdown(pausedFor)}
          </p>
        )}
      </Card>
    </div>
  );
}

// A free seat that staff opened. With a timer it counts down to the relock;
// without one it says so, because the only thing that closes it is a person.
function UnblockedNotice({ station }: { station: Station }) {
  const untilRelock = useDeadline(station.unlocked_until);
  if (!isUnblocked(station)) return null;
  if (untilRelock === null || !station.unlocked_until) {
    return (
      <Card className="border-sky-500/50 bg-sky-500/10">
        <p className="text-xs uppercase tracking-wide text-slate-500">Unblocked · no time limit</p>
        <p className="mt-1 text-sm text-sky-200">
          The screen stays open until you block it or seat a customer.
        </p>
      </Card>
    );
  }
  return (
    <Card className="border-sky-500/50 bg-sky-500/10">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500">Unblocked · locks again in</p>
          <p className="mt-1 font-mono text-3xl font-semibold tabular-nums text-sky-300">
            {formatCountdown(untilRelock)}
          </p>
        </div>
        <p className="text-sm text-slate-400">at {formatTime(station.unlocked_until)}</p>
      </div>
    </Card>
  );
}

function UnblockDialog({
  station,
  onClose,
  onDone,
}: {
  station: Station;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  // null = no limit: the owner sitting down to play, a long install.
  const [minutes, setMinutes] = useState<number | null>(30);
  const [custom, setCustom] = useState('');
  const [busy, setBusy] = useState(false);

  const chosen: number | null = custom.trim() !== '' ? Number(custom) : minutes;
  const valid = chosen === null || (Number.isInteger(chosen) && chosen >= 1 && chosen <= 480);

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const result = await api.stations.unblock(station.station_id, chosen);
      toast.success(
        result.unlocked_until
          ? `${result.name} is unblocked until ${formatTime(result.unlocked_until)}.`
          : `${result.name} is unblocked with no time limit. Block it when you are done.`,
      );
      onDone();
      onClose();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Unblock ${station.name}`} onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-slate-300">
          {station.station_type === 'ps5'
            ? 'Shows the PS5 on its TV with nobody seated and nothing charged'
            : 'Opens the PC with nobody seated and no time charged'}{' '}
          — for installing a game, a quick trial, checking a repair, or playing
          yourself. With a timer it locks itself again when the time runs out;
          with no limit it stays open until you block it.
        </p>
        <div className="grid grid-cols-5 gap-2">
          {[...UNBLOCK_PRESETS, null].map((preset) => (
            <button
              key={preset ?? 'none'}
              type="button"
              onClick={() => {
                setMinutes(preset);
                setCustom('');
              }}
              className={`min-h-11 rounded-lg border px-1 text-sm font-medium transition-colors ${
                custom === '' && minutes === preset
                  ? 'border-sky-500/60 bg-sky-500/15 text-sky-300'
                  : 'border-slate-800 text-slate-300 hover:bg-slate-800'
              }`}
            >
              {preset === null ? 'No limit' : formatMinutes(preset)}
            </button>
          ))}
        </div>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-slate-300">
            Or a custom number of minutes (up to 480)
          </span>
          <input
            value={custom}
            onChange={(event) => setCustom(event.target.value.replace(/[^0-9]/g, ''))}
            inputMode="numeric"
            placeholder="45"
            className="min-h-11 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 text-base text-slate-100 placeholder:text-slate-500 focus:border-sky-500 focus:outline-none"
          />
        </label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button className="flex-1" disabled={busy || !valid} onClick={submit}>
            {busy
              ? 'Unblocking…'
              : chosen === null
                ? 'Unblock with no limit'
                : `Unblock for ${valid ? formatMinutes(chosen) : '…'}`}
          </Button>
          <Button variant="secondary" className="flex-1" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// Orders are mostly taken while the customer is playing, so the tab sits here
// rather than a trip away on the customer card. The due is the customer's
// whole unpaid tab — food, drinks and recharges — at selling price, from the
// floor poll.
function SessionTabCard({
  session,
  busy,
  expanded,
  onAdd,
  onToggle,
}: {
  session: NonNullable<Station['active_session']>;
  busy: boolean;
  expanded: boolean;
  onAdd: () => void;
  onToggle: () => void;
}) {
  const due = session.fnb_due_amount ?? 0;
  return (
    <Card className={due > 0 ? 'border-amber-500/40 bg-amber-500/5' : undefined}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500">Tab</p>
          <p className={`mt-1 text-2xl font-semibold ${due > 0 ? 'text-amber-300' : 'text-slate-500'}`}>
            {formatMoney(due)}
            <span className="ml-2 text-sm font-normal text-slate-500">due</span>
          </p>
        </div>
        <div className="flex gap-2">
          {!expanded && (
            <Button variant="secondary" disabled={busy} onClick={onAdd}>
              Add item
            </Button>
          )}
          {/* The full tab opens right here while the customer is playing; once
              the session is over it lives on the customer card. */}
          <Button variant="ghost" onClick={onToggle}>
            {expanded ? 'Hide tab' : 'View tab'}
          </Button>
        </div>
      </div>
    </Card>
  );
}

// A PS5 whose booked time ran out. The record has ended, but nothing can
// switch a console off — until someone confirms the game has stopped, this is
// the loudest thing on the floor. Rendered above the station list, so it is
// seen whichever seat is selected.
export function TimeUpCard({
  station,
  timeUp,
  onChanged,
}: {
  station: Station;
  timeUp: NonNullable<Station['time_up']>;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const sinceEnd = useElapsed(timeUp.ended_at);

  const acknowledge = async () => {
    setBusy(true);
    try {
      await api.sessions.acknowledgeTimeUp(timeUp.session_id);
      toast.success(`${station.name} is free for the next group.`);
      onChanged();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="animate-pulse border-2 border-rose-500 bg-rose-500/20">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-rose-200">
            Time's up
          </p>
          <p className="mt-1 text-lg font-semibold text-rose-100">
            Stop the game on {station.name}
          </p>
        </div>
        {/* Keeps counting until someone marks it free, so staff can see how
            much unpaid play has gone by. */}
        <div className="text-right">
          <p className="font-mono text-3xl font-bold tabular-nums text-rose-50">
            +{formatCountdown(sinceEnd)}
          </p>
          <p className="text-xs font-semibold uppercase tracking-wide text-rose-200">
            over time
          </p>
        </div>
      </div>
      <p className="mt-1 text-sm text-rose-100/80">
        {timeUp.customer_name}'s group has used all their booked time. A PS5 cannot
        lock itself — go over and end it, or start a new paid session for them.
      </p>
      <Button variant="danger" className="mt-3 w-full" disabled={busy} onClick={acknowledge}>
        {busy ? 'Saving…' : 'Game stopped — mark free'}
      </Button>
    </Card>
  );
}

// The seat's actions, fixed to the bottom of the screen whatever the panel
// holds — a long tab or almost nothing — so Stop / Seat are always in the same
// place. Fixed positioning leaves the panel's column, so the bar measures the
// spot it occupies in the panel and lines itself up with it: beside the list
// on desktop, full width on a phone. The in-flow spacer keeps the last of the
// panel's content from hiding behind the bar. Buttons size to their labels.
function ActionBar({ children }: { children: ReactNode }) {
  const spacer = useRef<HTMLDivElement>(null);
  const bar = useRef<HTMLDivElement>(null);
  const [frame, setFrame] = useState<{ left: number; width: number; height: number } | null>(null);

  useLayoutEffect(() => {
    const measure = () => {
      if (!spacer.current || !bar.current) return;
      const { left, width } = spacer.current.getBoundingClientRect();
      const height = bar.current.offsetHeight;
      setFrame((current) =>
        current && current.left === left && current.width === width && current.height === height
          ? current
          : { left, width, height },
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (spacer.current) observer.observe(spacer.current);
    if (bar.current) observer.observe(bar.current);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  return (
    <>
      <div ref={spacer} style={{ height: frame?.height ?? 0 }} aria-hidden="true" />
      <div
        ref={bar}
        className="fixed bottom-0 z-20 pb-3 pt-2"
        // A zero-width spacer means the panel itself is hidden (on a phone
        // while the list is showing), so the bar must not float over the list.
        style={
          frame
            ? { left: frame.left, width: frame.width, display: frame.width === 0 ? 'none' : undefined }
            : { left: 16, right: 16 }
        }
      >
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-800 bg-slate-900/95 p-2 shadow-lg shadow-black/40 backdrop-blur">
          {children}
        </div>
      </div>
    </>
  );
}

// Everything staff can do to one seat, on the right of the floor list.
export function StationDetail({
  station,
  onChanged,
  onSeat,
  onStop,
  onBack,
}: {
  station: Station;
  /** Fired after any action so the floor refetches. */
  onChanged: () => void;
  onSeat: (station: Station) => void;
  onStop: (station: Station) => void;
  /** Return to the list. Only rendered on phone widths, where the panel
   *  replaces the list instead of sitting beside it. */
  onBack: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [unblocking, setUnblocking] = useState(false);
  const [shuttingDown, setShuttingDown] = useState(false);
  const { isAdmin } = useAuth();
  const [ordering, setOrdering] = useState(false);
  // Open by default: while someone is playing, their bill is the thing staff
  // most often need. "Hide tab" folds it away for a cleaner panel.
  const [tabOpen, setTabOpen] = useState(true);
  // The recharge dialog wants the customer card (balances by pool); it is
  // fetched on demand rather than carried on every floor poll.
  const [recharging, setRecharging] = useState<CustomerCard | null>(null);
  const [loadingCard, setLoadingCard] = useState(false);
  const [addingTime, setAddingTime] = useState(false);
  // A pack sold from this panel lands on the tab shown below it; the tab
  // section refetches when this changes.
  const [tabVersion, setTabVersion] = useState(0);

  const session = station.active_session;
  const isPs5 = station.station_type === 'ps5';
  const maintenance = station.status === 'maintenance';
  const fault = agentFault(station);
  const state = stationState(station);

  const run = async (action: () => Promise<string>) => {
    setBusy(true);
    try {
      toast.success(await action());
      onChanged();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const pause = () =>
    run(async () => {
      const result = await api.sessions.pause(session!.session_id);
      return isPs5
        ? station.agent.paired
          ? `${result.customer_name}'s session on ${result.station_name} is paused. The clock is stopped and the TV screen is locked.`
          : `${result.customer_name}'s session on ${result.station_name} is paused. The clock is stopped — tell the group, the console is not locked.`
        : `${result.customer_name}'s session on ${result.station_name} is paused. The PC is locked and no time is being charged.`;
    });
  const resume = () =>
    run(async () => {
      const result = await api.sessions.resume(session!.session_id);
      return `${result.customer_name} is back on ${result.station_name} — ${formatMinutes(result.minutes_remaining ?? 0)} left.`;
    });
  const block = () =>
    run(async () => {
      const result = await api.stations.block(station.station_id);
      return `${result.name} is locked.`;
    });

  const openRecharge = async () => {
    if (!session) return;
    setLoadingCard(true);
    try {
      setRecharging(await api.customers.get(session.customer_id));
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setLoadingCard(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <Button
            variant="ghost"
            className="-ml-2 px-2 lg:hidden"
            onClick={onBack}
            aria-label="Back to the list"
          >
            ←
          </Button>
          <div>
            <h2 className="text-xl font-semibold text-slate-100">{station.name}</h2>
            <p className="text-sm text-slate-500">
              {STATION_TYPE_LABEL[station.station_type] ?? station.station_type}
              {station.hostname && ` · ${station.hostname}`}
              {station.ip_address && ` · ${station.ip_address}`}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Badge tone={state.tone}>{state.label}</Badge>
          {/* An unpaired PS5 is timer-only: no agent badge. A PS5 with its
              TV paired shows the TV like a PC's agent. */}
          {(!isPs5 || station.agent.paired) && (
            <Badge tone={!station.agent.paired ? 'slate' : station.agent.online ? 'green' : 'amber'}>
              {!station.agent.paired
                ? 'Not paired'
                : isPs5
                  ? station.agent.online ? 'TV online' : 'TV offline'
                  : station.agent.online ? 'Agent online' : 'Agent offline'}
            </Badge>
          )}
          {station.agent.paired && (
            <Badge tone={station.agent.locked ? 'slate' : 'sky'}>
              {station.agent.locked ? 'Screen locked' : 'Screen open'}
            </Badge>
          )}
        </div>
      </div>

      {fault && (
        <Card className="border-amber-500/50 bg-amber-500/10">
          <p className="text-sm text-amber-200">
            {isPs5
              ? 'This TV is not reporting in — the screen will not blank or open until it does. Check the TV is on and the HHG Station app is running.'
              : 'This PC is not reporting in — the desktop will not lock or unlock until it does. Go and check the machine.'}
          </p>
        </Card>
      )}

      {session ? (
        <>
          <Card>
            <p className="text-xs uppercase tracking-wide text-slate-500">Seated</p>
            <Link
              to={`/customers/${session.customer_id}`}
              className="mt-1 block text-lg font-semibold text-sky-300 hover:underline"
            >
              {session.customer_name}
            </Link>
            <p className="text-sm text-slate-500">
              {session.customer_phone} · since {formatTime(session.start_time)}
              {session.paused && session.paused_at && ` · paused at ${formatTime(session.paused_at)}`}
            </p>
            {isPs5 && session.player_count !== null && (
              <p className="mt-1 text-sm text-slate-300">
                {session.player_count} player{session.player_count === 1 ? '' : 's'} ·{' '}
                {formatMinutes(Math.round((session.booked_seconds ?? 0) / 60))} booked ·{' '}
                {formatMoney(session.amount_charged)} on the tab
              </p>
            )}
          </Card>

          <SessionClocks station={station} />

          <SessionTabCard
            session={session}
            busy={busy}
            expanded={tabOpen}
            onAdd={() => setOrdering(true)}
            onToggle={() => setTabOpen((open) => !open)}
          />

          {tabOpen && (
            <div className="rounded-xl border border-slate-800 p-4">
              <CustomerTabSection
                customerId={session.customer_id}
                customerName={session.customer_name}
                isActive
                onChanged={onChanged}
                refreshKey={tabVersion}
              />
              <Link
                to={`/customers/${session.customer_id}`}
                className="mt-3 inline-block text-sm text-sky-300 hover:underline"
              >
                Open {session.customer_name}'s full card →
              </Link>
            </div>
          )}

          {session.paused && (
            <p className="text-sm text-amber-200">
              {isPs5
                ? 'The clock is stopped. The console is not locked — make sure the group has actually stopped playing.'
                : 'The clock is stopped and the PC is locked. Nothing is being charged until you resume.'}
            </p>
          )}

          {session.paused && maintenance && (
            <p className="text-xs text-slate-500">
              Under maintenance — put the station back in service before resuming.
            </p>
          )}

          <ActionBar>
            {/* A customer asking for more time mid-session is the normal
                case; the pack rolls into the running session on the next
                tick and its price goes on the tab. A PS5 is not paid from
                wallet minutes: the group buys another booking instead, which
                stacks on the time they have left. */}
            {isPs5 ? (
              <Button disabled={busy} onClick={() => setAddingTime(true)}>
                Add time
              </Button>
            ) : (
              <Button disabled={busy || loadingCard} onClick={openRecharge}>
                {loadingCard ? 'Opening…' : 'Recharge'}
              </Button>
            )}
            {session.paused ? (
              <Button variant="secondary" disabled={busy || maintenance} onClick={resume}>
                Resume session
              </Button>
            ) : (
              <Button variant="secondary" disabled={busy} onClick={pause}>
                Pause session
              </Button>
            )}
            <Button
              variant="danger"
              className="ml-auto"
              disabled={busy}
              onClick={() => onStop(station)}
            >
              Stop session
            </Button>
          </ActionBar>
        </>
      ) : (
        <>
          <UnblockedNotice station={station} />

          <Card>
            <p className="text-sm text-slate-300">
              {maintenance
                ? 'Out of service. An admin can put it back in service under Stations.'
                : isPs5 && !station.agent.paired
                  ? 'Free. Start a group: pick the players and how long, and the price goes on the payer\'s tab.'
                  : isUnblocked(station)
                    ? 'Open with nobody seated. Block it to lock the screen now, or seat a customer.'
                    : 'Free and locked. Seat a customer, or unblock it with nobody on it.'}
            </p>
          </Card>

          <ActionBar>
            <Button disabled={busy || maintenance} onClick={() => onSeat(station)}>
              {isPs5 ? 'Start a group' : 'Seat a customer'}
            </Button>
            {/* Block/unblock are the free-seat controls. A seat with a customer
                on it is paused instead, which locks it the same way. A PS5
                gets them once its TV is paired; without one there is no lock. */}
            {(!isPs5 || station.agent.paired) && (
              <>
                <Button
                  variant="secondary"
                  disabled={busy || maintenance}
                  onClick={() => setUnblocking(true)}
                >
                  {isUnblocked(station) ? 'Change unblock' : 'Unblock device'}
                </Button>
                <Button
                  variant="secondary"
                  disabled={busy || !isUnblocked(station)}
                  onClick={block}
                  title={isUnblocked(station) ? undefined : 'Already locked'}
                >
                  Block device
                </Button>
                {/* Admin-only on the backend too. Closing time is the usual
                    reason: switch the free seats off from the counter. */}
                {isAdmin && !isPs5 && station.agent.paired && (
                  <Button
                    variant="ghost"
                    disabled={busy}
                    onClick={() => setShuttingDown(true)}
                    className="ml-auto text-rose-300 hover:text-rose-200"
                  >
                    {station.shutdown_requested_at ? 'Shutting down… send again' : 'Shut down PC'}
                  </Button>
                )}
              </>
            )}
          </ActionBar>
        </>
      )}

      {recharging && (
        <SellRechargeDialog
          customer={recharging}
          initialPool={(poolForStationType(station.station_type) ?? 'general') as Pool}
          onClose={() => setRecharging(null)}
          onSold={() => {
            setTabVersion((version) => version + 1);
            onChanged();
          }}
        />
      )}
      {addingTime && session && (
        <AddPs5TimeDialog
          station={station}
          session={session}
          onClose={() => setAddingTime(false)}
          onAdded={() => {
            setTabVersion((version) => version + 1);
            onChanged();
          }}
        />
      )}
      {ordering && session && (
        <AddItemDialog
          customerId={session.customer_id}
          customerName={session.customer_name}
          onClose={() => setOrdering(false)}
          onAdded={onChanged}
        />
      )}
      {shuttingDown && (
        <ShutdownDialog
          station={station}
          onClose={() => setShuttingDown(false)}
          onDone={onChanged}
        />
      )}
      {unblocking && (
        <UnblockDialog
          station={station}
          onClose={() => setUnblocking(false)}
          onDone={onChanged}
        />
      )}
    </div>
  );
}
